/**
 * 服务器监控（C 端 server Workers Logs 报错监控后台页）— 可查看的 service 闭集、查询参数与只读响应形状。
 *
 * 接口：`GET /api/v1/server-monitor/overview`（健康五卡 + 告警 + 规则 + 通知自检 + 支付到账时效）、
 * `GET /api/v1/server-monitor/logs`（Telemetry 错误日志 cursor 分页）。仅 root / admin 可读（cause 含内部错误信息）。
 *
 * 支付到账时效（同一 cron 的第二个数据源）：只读查 C 端 webhook 事件表近 window_minutes 的 Production 购买 / 续费事件，
 * 按 provider 算处理耗时 p95 / 积压 / Adapty 送达 p95，告警进同一套状态机（类别 payment）。
 *
 * C 端接口时效（同一 cron 的第三个数据源）：Telemetry calculations 按 `$metadata.trigger` 分组算近 window_minutes
 * 的 count / p50 / p95 / p99——接口取 C 端 `request.complete` 日志的 latencyMs，队列取调用级记录的 wallTimeMs；
 * 超阈值进同一套状态机（核心接口 P0、其他接口与队列 P1）。
 * 字段 snake_case；时间一律 ISO 8601（UTC `Z`）。
 */
import { z } from 'zod';

/** 可查看的 C 端 Worker 服务名闭集（Telemetry `$metadata.service`）*/
export const SERVER_MONITOR_SERVICES = [
  'novel-app-server-production',
  'novel-app-server-dev',
] as const;
export const ServerMonitorServiceSchema = z.enum(SERVER_MONITOR_SERVICES);
export type ServerMonitorService = z.infer<typeof ServerMonitorServiceSchema>;

const PRODUCTION_SERVICE: ServerMonitorService = 'novel-app-server-production';
const DEV_SERVICE: ServerMonitorService = 'novel-app-server-dev';

/**
 * 本环境后台可查看的 service（前后端共用）：本环境 cron 的监控目标（`SERVER_MONITOR_TARGET_SERVICE`）永远可看；
 * 只有生产后台（目标为 production）另可看开发环境；目标未配置或不在闭集 → 空。
 * dev 后台不能读 prod 日志：日志 cause 含内部错误信息，开发环境的账号与凭证面不应触达生产数据。
 * 首项即本环境目标（overview 不传 service 时的缺省）。
 */
export function serverMonitorViewableServices(
  targetService: string | null | undefined,
): ServerMonitorService[] {
  const target = targetService?.trim();
  if (target === PRODUCTION_SERVICE) return [PRODUCTION_SERVICE, DEV_SERVICE];
  if (target === DEV_SERVICE) return [DEV_SERVICE];
  return [];
}

/** 告警推送的飞书群展示名（只展示，不含 webhook）*/
export const SERVER_MONITOR_FEISHU_GROUP_NAMES: Record<ServerMonitorService, string> = {
  'novel-app-server-production': 'BigThree-服务器监控',
  'novel-app-server-dev': 'BigThree-服务器监控【dev】',
};

/** 报错类别闭集，顺序 = 健康卡顺序（与 server `services/server-monitor-rules.ts` 一致）*/
export const SERVER_MONITOR_CATEGORIES = [
  'auth',
  'reading',
  'payment',
  'platform',
  'other',
] as const;
export const ServerMonitorCategorySchema = z.enum(SERVER_MONITOR_CATEGORIES);
export type ServerMonitorCategory = z.infer<typeof ServerMonitorCategorySchema>;

export const SERVER_MONITOR_LOG_RANGES = ['15m', '1h', '24h', '7d'] as const;
export const ServerMonitorLogRangeSchema = z.enum(SERVER_MONITOR_LOG_RANGES);
export type ServerMonitorLogRange = z.infer<typeof ServerMonitorLogRangeSchema>;
export const SERVER_MONITOR_LOG_RANGE_MS: Record<ServerMonitorLogRange, number> = {
  '15m': 15 * 60_000,
  '1h': 60 * 60_000,
  '24h': 24 * 60 * 60_000,
  '7d': 7 * 24 * 60 * 60_000,
};

export const SERVER_MONITOR_LOG_LEVELS = ['error', 'warn'] as const;
export const ServerMonitorLogLevelSchema = z.enum(SERVER_MONITOR_LOG_LEVELS);
export type ServerMonitorLogLevel = z.infer<typeof ServerMonitorLogLevelSchema>;

/** 日志每页条数（Telemetry 拿不到总数，cursor 翻页）*/
export const SERVER_MONITOR_LOG_PAGE_LIMIT = 50;

/**
 * C 端限流降级放行事件（warn 级）。「同一主体降级放行突增」告警由它产生，指纹为 `<事件>|-|-|-`；
 * 后台从该告警下钻日志须带 warn 级别，否则按 error 过滤会查不到触发记录。
 */
export const SERVER_MONITOR_RATE_LIMIT_DEGRADED_EVENT = 'ratelimit.do.degraded';
/**
 * 后台自身的「降级放行按主体统计查询持续失败」告警事件：不是 C 端日志，告警没有可下钻的 C 端日志。
 */
export const SERVER_MONITOR_RATE_LIMIT_DEGRADED_QUERY_FAILED_EVENT =
  'server_monitor.ratelimit_degraded.query_failed';
/** 日志 cause 序列化后的展示上限（字符）*/
export const SERVER_MONITOR_LOG_CAUSE_MAX_CHARS = 4096;
/** 健康卡近 1h 分布：12 根柱 × 5 分钟 */
export const SERVER_MONITOR_HEALTH_BUCKETS = 12;
export const SERVER_MONITOR_HEALTH_BUCKET_MINUTES = 5;

/** `a,b` 逗号分隔多值 → 去重数组；空串视为未传 */
function csvEnum<T extends readonly [string, ...string[]]>(values: T) {
  return z
    .string()
    .max(200)
    .transform((raw) => [
      ...new Set(
        raw
          .split(',')
          .map((part) => part.trim())
          .filter(Boolean),
      ),
    ])
    .pipe(z.array(z.enum(values)));
}

export const ServerMonitorOverviewQuerySchema = z.object({
  /** 缺省 = 本环境监控目标；不在本环境可查看列表 → 422 FORBIDDEN_OPERATION */
  service: ServerMonitorServiceSchema.optional(),
});
export type ServerMonitorOverviewQuery = z.infer<typeof ServerMonitorOverviewQuerySchema>;

export const ServerMonitorLogsQuerySchema = z.object({
  /** 不在本环境可查看列表 → 422 FORBIDDEN_OPERATION */
  service: ServerMonitorServiceSchema,
  range: ServerMonitorLogRangeSchema.default('1h'),
  /** 逗号分隔，默认只看 error */
  level: csvEnum(SERVER_MONITOR_LOG_LEVELS)
    .optional()
    .transform((v) => (v && v.length > 0 ? v : (['error'] as ServerMonitorLogLevel[]))),
  /** 逗号分隔；空 = 全部类别 */
  category: csvEnum(SERVER_MONITOR_CATEGORIES)
    .optional()
    .transform((v) => v ?? []),
  /**
   * Telemetry needle 全文检索（event / 路径 / requestId / errorCode 等）。
   * 形如告警指纹 `event|errorCode|method|归一化 path[|任务类型]` 时按指纹精确过滤：needle 只用 event 段，
   * `platform.<outcome>` 改按 `$workers.outcome` 过滤、`log.unstructured` 不下推 needle。
   */
  q: z
    .string()
    .trim()
    .max(200)
    .optional()
    .transform((v) => (v ? v : undefined)),
  /** 上一页响应的 next_cursor（不透明）*/
  cursor: z.string().min(1).max(512).optional(),
});
export type ServerMonitorLogsQuery = z.infer<typeof ServerMonitorLogsQuerySchema>;

const SeveritySchema = z.enum(['P0', 'P1']);
const IsoSchema = z.string();

/**
 * 支付到账时效的 provider 闭集（server `services/server-monitor-payment-latency-rules.ts` 同源）：
 * adapty = adapty_webhook_events（App 内购，含原生 Stripe 校验后的发币）；stripe = 原生 Stripe webhook →
 * Adapty 校验提交（stripe_webhook_events × stripe_validation_triggers）；web_stripe = Web Stripe 直接结算（web_stripe_events）。
 */
export const SERVER_MONITOR_PAYMENT_PROVIDERS = ['adapty', 'stripe', 'web_stripe'] as const;
export const ServerMonitorPaymentProviderSchema = z.enum(SERVER_MONITOR_PAYMENT_PROVIDERS);
export type ServerMonitorPaymentProvider = z.infer<typeof ServerMonitorPaymentProviderSchema>;

/**
 * 时效指标闭集：processing = 处理耗时 p95；backlog = 积压条数；delivery = Adapty 回调送达 p95；
 * callback_missing = 原生 Stripe 校验成功后同一用户确定没有任何 Stripe 到账（保守判据，只有 stripe）
 */
export const SERVER_MONITOR_PAYMENT_METRICS = [
  'processing',
  'backlog',
  'delivery',
  'callback_missing',
] as const;
export const ServerMonitorPaymentMetricSchema = z.enum(SERVER_MONITOR_PAYMENT_METRICS);
export type ServerMonitorPaymentMetric = z.infer<typeof ServerMonitorPaymentMetricSchema>;

/** 时效告警的指标详情（日志类告警为 null）；不含 payload / 用户数据 */
export const ServerMonitorAlertLatencySchema = z.object({
  provider: ServerMonitorPaymentProviderSchema,
  metric: ServerMonitorPaymentMetricSchema,
  /** 最近一次超阈值时的实际值：p95 指标为毫秒，backlog 为条数 */
  value: z.number().nonnegative(),
  /** 阈值（同 value 单位）：超过即告警 */
  threshold: z.number().nonnegative(),
  unit: z.enum(['ms', 'count']),
  /** processing / delivery 为 p95 的样本数；backlog 为积压条数；callback_missing 为到账缺失条数 */
  samples: z.number().int().nonnegative(),
  window_minutes: z.number().int().positive(),
  /** 最慢一笔（backlog = 等待最久的一笔）的 C 端事件表主键与 received_at */
  slowest_event_id: z.string().nullable(),
  slowest_received_at: IsoSchema.nullable(),
});
export type ServerMonitorAlertLatency = z.infer<typeof ServerMonitorAlertLatencySchema>;

/** 接口时效的对象类型：route = HTTP 接口（request.complete 的 latencyMs）；queue = 队列批处理（调用级 wallTimeMs）*/
export const SERVER_MONITOR_API_LATENCY_KINDS = ['route', 'queue'] as const;
export const ServerMonitorApiLatencyKindSchema = z.enum(SERVER_MONITOR_API_LATENCY_KINDS);
export type ServerMonitorApiLatencyKind = z.infer<typeof ServerMonitorApiLatencyKindSchema>;

/** 接口时效告警的指标详情（其余告警为 null）；trigger 是 Telemetry 归一后的 `METHOD /path`（id 段为 REDACTED）或队列名 */
export const ServerMonitorAlertApiLatencySchema = z.object({
  kind: ServerMonitorApiLatencyKindSchema,
  trigger: z.string(),
  /** 最近一次超阈值时的分位数（毫秒）*/
  p50_ms: z.number().nonnegative().nullable(),
  p95_ms: z.number().nonnegative(),
  p99_ms: z.number().nonnegative().nullable(),
  samples: z.number().int().nonnegative(),
  /** p95 超过即告警（毫秒）*/
  threshold_ms: z.number().nonnegative(),
  window_minutes: z.number().int().positive(),
});
export type ServerMonitorAlertApiLatency = z.infer<typeof ServerMonitorAlertApiLatencySchema>;

/**
 * 同一主体降级放行突增告警的统计（其余告警为 null）：取 last_seen_at 所在评估轮次（最近一次达标）的值，
 * 不含任何计数主体标识。
 */
export const ServerMonitorAlertRateLimitDegradedSchema = z.object({
  /** 窗口内降级放行次数 ≥ threshold 的计数主体数；flagged_subjects_capped 为 true 时为下界 */
  flagged_subjects: z.number().int().nonnegative(),
  flagged_subjects_capped: z.boolean(),
  /** 达标主体中的最高降级放行次数 */
  max_count: z.number().int().nonnegative(),
  /** 达标判据：单个主体窗口内次数 ≥ 本值（该轮 repeat_threshold）*/
  threshold: z.number().int().positive(),
  /** 统计窗口：截至 last_seen_at 的分钟数（该轮 window_minutes）*/
  window_minutes: z.number().int().positive(),
  /** 计数来自日志平台采样估算 */
  sampled: z.boolean(),
});
export type ServerMonitorAlertRateLimitDegraded = z.infer<
  typeof ServerMonitorAlertRateLimitDegradedSchema
>;

export const ServerMonitorHealthCategorySchema = z.object({
  category: ServerMonitorCategorySchema,
  /** firing = 有活跃告警；warn = 近 1h 有错误；ok = 近 1h 无错误 */
  status: z.enum(['ok', 'warn', 'firing']),
  last_hour_count: z.number().int().nonnegative(),
  last_24h_count: z.number().int().nonnegative(),
  last_occurred_at: IsoSchema.nullable(),
  /** 近 1h 每 5 分钟一桶，最早在前，长度 SERVER_MONITOR_HEALTH_BUCKETS */
  buckets: z.array(z.number().int().nonnegative()),
});
export type ServerMonitorHealthCategory = z.infer<typeof ServerMonitorHealthCategorySchema>;

export const ServerMonitorHealthSchema = z.object({
  /** unconfigured = admin 缺 Telemetry token / account；query_failed = 本次查询失败 */
  state: z.enum(['ok', 'unconfigured', 'query_failed']),
  computed_at: IsoSchema.nullable(),
  cache_hit: z.boolean(),
  /** 近 24h error 事件超出 5000 条上限被截断（计数偏小）*/
  truncated: z.boolean(),
  sampled: z.boolean(),
  raw_events: z.number().int().nonnegative(),
  categories: z.array(ServerMonitorHealthCategorySchema),
});
export type ServerMonitorHealth = z.infer<typeof ServerMonitorHealthSchema>;

export const ServerMonitorAlertSchema = z.object({
  fingerprint: z.string(),
  severity: SeveritySchema,
  category: ServerMonitorCategorySchema,
  /** new = 已告警后未再出现；ongoing = 告警后仍在出现；recovered = 已恢复 */
  status: z.enum(['new', 'ongoing', 'recovered']),
  event: z.string(),
  error_code: z.string().nullable(),
  http_status: z.number().int().nullable(),
  method: z.string().nullable(),
  /** 归一化路径（资源 id 段为 `:id`）*/
  path: z.string().nullable(),
  request_id: z.string().nullable(),
  /** 窗口（window_minutes）内次数；健康数据不可用时为 null */
  window_count: z.number().int().nonnegative().nullable(),
  total_count: z.number().int().nonnegative(),
  first_seen_at: IsoSchema,
  last_seen_at: IsoSchema,
  /** 最近一次成功推送飞书的时间；null = 尚未送达（下一轮重发）*/
  last_notified_at: IsoSchema.nullable(),
  notification_count: z.number().int().nonnegative(),
  recovered_at: IsoSchema.nullable(),
  /** 支付到账时效告警的指标详情；日志类告警为 null（时效告警的 method / path / event 无 HTTP 含义）*/
  latency: ServerMonitorAlertLatencySchema.nullable(),
  /** 接口时效告警的指标详情；其余告警为 null */
  api_latency: ServerMonitorAlertApiLatencySchema.nullable(),
  /** 同一主体降级放行突增告警的统计；其余告警（含其统计查询失败告警）为 null */
  rate_limit_degraded: ServerMonitorAlertRateLimitDegradedSchema.nullable(),
  /** 已恢复列表里：不是确认恢复，而是接口时效告警长期无流量关闭（未确认恢复）；其余为 false */
  closed_no_traffic: z.boolean(),
  /** 已恢复列表里：恢复 / 关闭通知是否推送过飞书（短暂故障静默落账为 false）；活跃告警恒为 false */
  recovery_notified: z.boolean(),
});
export type ServerMonitorAlert = z.infer<typeof ServerMonitorAlertSchema>;

/** 「持续中」重推间隔上限（分钟）*/
export const SERVER_MONITOR_ONGOING_RENOTIFY_MAX_MINUTES = 240;

/**
 * 同一告警下一次「持续中」重推前要等的分钟数：第 n 次送达后等 冷却 × 2^(n−1)，封顶 4 小时
 * （默认冷却 30 分钟 → 30 / 60 / 120 / 240 …）。长期不恢复的告警不再每 30 分钟刷一条；
 * cron 与后台告警卡共用这一个公式，展示的冷却与实际推送一致。旧状态缺 notificationCount 按 1 次算。
 */
export function serverMonitorOngoingRenotifyMinutes(
  cooldownMinutes: number,
  notificationCount: number,
): number {
  const exponent = Math.max(0, Math.max(1, notificationCount) - 1);
  // 冷却本身配得比上限还长时以冷却为准（退避只拉长、不缩短）
  return Math.min(
    cooldownMinutes * 2 ** exponent,
    Math.max(cooldownMinutes, SERVER_MONITOR_ONGOING_RENOTIFY_MAX_MINUTES),
  );
}

/**
 * 「持续中」不退避（仍按冷却重推）的告警：支付域全部 P0 日志报错（webhook / 解锁 / 购买 / 支付任务死信 / 支付查询接口等，
 * 整类豁免而不按写链路白名单——宁可只读接口持续报错时多推几条，也不因白名单漏加让付费链路故障被退避）、到账积压、
 * 校验后到账缺失。退避到 4 小时一次会让「付了钱没到账」在间隔里越积越多没人看见。
 * 不含处理耗时 p95 与接口时效：那是「慢」不是「丢」，照常退避。cron 与后台告警卡共用这一判据。
 */
export function serverMonitorOngoingBackoffExempt(alert: {
  category: ServerMonitorCategory;
  severity: 'P0' | 'P1';
  /** 支付到账时效告警的指标；日志 / 接口时效告警为 null */
  paymentMetric: ServerMonitorPaymentMetric | null;
  /** 是否接口时效告警 */
  apiLatency: boolean;
}): boolean {
  if (alert.category !== 'payment' || alert.severity !== 'P0' || alert.apiLatency) return false;
  return (
    alert.paymentMetric === null ||
    alert.paymentMetric === 'backlog' ||
    alert.paymentMetric === 'callback_missing'
  );
}

export const ServerMonitorThresholdsSchema = z.object({
  core_threshold: z.number().int().positive(),
  repeat_threshold: z.number().int().positive(),
  window_minutes: z.number().int().positive(),
  cooldown_minutes: z.number().int().positive(),
  recovery_minutes: z.number().int().positive(),
});
export type ServerMonitorThresholds = z.infer<typeof ServerMonitorThresholdsSchema>;

export const ServerMonitorRuleSchema = z.object({
  severity: SeveritySchema,
  name: z.string(),
  categories: z.array(ServerMonitorCategorySchema),
  /** category = 类别内任一指纹计数；fingerprint = 同指纹计数 */
  scope: z.enum(['category', 'fingerprint']),
  threshold: z.number().int().positive(),
  note: z.string(),
});
export type ServerMonitorRule = z.infer<typeof ServerMonitorRuleSchema>;

export const ServerMonitorPaymentThresholdsSchema = z.object({
  /** 处理耗时 p95 超过即 P0（秒）*/
  processing_p95_seconds: z.number().int().positive(),
  /** 积压条数超过即 P0（默认 0 = 有一条就告警）*/
  backlog_count: z.number().int().nonnegative(),
  /** 到达多久仍未生效算积压（秒）*/
  backlog_age_seconds: z.number().int().positive(),
  /** 积压回看时长（小时，独立于 p95 的 window_minutes：卡住的单笔被处理前一直算积压）*/
  backlog_lookback_hours: z.number().int().positive(),
  /** Adapty 回调送达 p95 超过即 P1（秒）*/
  delivery_p95_seconds: z.number().int().positive(),
  /** p95 至少需要的样本数；不足只判积压 */
  min_samples: z.number().int().positive(),
});
export type ServerMonitorPaymentThresholds = z.infer<typeof ServerMonitorPaymentThresholdsSchema>;

export const ServerMonitorPaymentRuleSchema = z.object({
  severity: SeveritySchema,
  metric: ServerMonitorPaymentMetricSchema,
  name: z.string(),
  providers: z.array(ServerMonitorPaymentProviderSchema),
  note: z.string(),
});
export type ServerMonitorPaymentRule = z.infer<typeof ServerMonitorPaymentRuleSchema>;

export const ServerMonitorApiLatencyThresholdsSchema = z.object({
  /** 核心接口（注册登录 / 阅读 / 支付）p95 超过即 P0（毫秒）*/
  core_p95_ms: z.number().int().positive(),
  /** 其他接口 p95 超过即 P1（毫秒）*/
  other_p95_ms: z.number().int().positive(),
  /** 队列批处理 p95 超过即 P1（毫秒）*/
  queue_p95_ms: z.number().int().positive(),
  /** 接口 p95 至少需要的请求数；不足为未知 */
  min_samples: z.number().int().positive(),
  /** 队列 p95 至少需要的批次数 */
  queue_min_samples: z.number().int().positive(),
  /** 队列查询的回看窗口（分钟；独立于接口的 window_minutes）*/
  queue_window_minutes: z.number().int().positive(),
  /** 单次查询的分组上限：超过即截断，没拿到的组为未知 */
  group_limit: z.number().int().positive(),
  /** 已送达的接口时效告警连续多久无有效观测（无请求 / 样本不足）按无流量关闭 */
  no_traffic_close_minutes: z.number().int().positive(),
});
export type ServerMonitorApiLatencyThresholds = z.infer<
  typeof ServerMonitorApiLatencyThresholdsSchema
>;

export const ServerMonitorApiLatencyRuleSchema = z.object({
  severity: SeveritySchema,
  /** core = 核心接口；other = 其他接口；queue = 队列 */
  scope: z.enum(['core', 'other', 'queue']),
  name: z.string(),
  note: z.string(),
});
export type ServerMonitorApiLatencyRule = z.infer<typeof ServerMonitorApiLatencyRuleSchema>;

/** 最近一轮单个接口 / 队列的分位数（cron 写入）*/
export const ServerMonitorApiLatencyItemSchema = z.object({
  kind: ServerMonitorApiLatencyKindSchema,
  trigger: z.string(),
  /** 接口按路径归类（auth / reading / payment / other）；队列为 platform */
  category: ServerMonitorCategorySchema,
  /** 接口返回的条数（有摄取采样时是放大后的估算值）*/
  count: z.number().int().nonnegative(),
  /** 真实入库样本数 = count / sampleInterval；样本门槛按它判 */
  samples: z.number().int().nonnegative(),
  /** 该组有摄取采样（sampleInterval > 1）：页面标「采样」*/
  sampled: z.boolean(),
  p50_ms: z.number().nonnegative().nullable(),
  p95_ms: z.number().nonnegative().nullable(),
  p99_ms: z.number().nonnegative().nullable(),
  /** 适用阈值（毫秒）与超阈值时的级别 */
  threshold_ms: z.number().nonnegative(),
  severity: SeveritySchema,
  /** breach = 超阈值；normal = 样本足够且未超；insufficient = 样本不足 / p95 缺失（未知，不判）*/
  verdict: z.enum(['breach', 'normal', 'insufficient']),
});
export type ServerMonitorApiLatencyItem = z.infer<typeof ServerMonitorApiLatencyItemSchema>;

/** 单次 calculations 查询（接口 / 队列各一次）的结果状态 */
export const ServerMonitorApiLatencyQuerySchema = z.object({
  /** sampled = 被 Cloudflare 采样，整次结果按未知处理 */
  state: z.enum(['ok', 'query_failed', 'sampled']),
  /** TELEMETRY_HTTP_STATUS 等，不含 token */
  error_code: z.string().nullable(),
  /** 查询成功但不可判定（ABR 采样，或有组但全部组判不了）*/
  degraded: z.boolean(),
  /** 拿到的分组数（聚合层已按样本门槛过滤）*/
  groups: z.number().int().nonnegative(),
  /** 分组数超过上限：没拿到的组（p95 更低的尾部）为未知 */
  truncated: z.boolean(),
});
export type ServerMonitorApiLatencyQuery = z.infer<typeof ServerMonitorApiLatencyQuerySchema>;

export const ServerMonitorApiLatencySchema = z.object({
  /**
   * ok = 两次查询都成功；partial = 一次失败或被采样；query_failed = 两次都失败；
   * unconfigured = admin 缺 Telemetry token / account；not_run = cron 尚未写入结果
   */
  state: z.enum(['ok', 'partial', 'query_failed', 'unconfigured', 'not_run']),
  computed_at: IsoSchema.nullable(),
  /** 接口查询的窗口（分钟）*/
  window_minutes: z.number().int().positive(),
  /** 队列查询的窗口（分钟）*/
  queue_window_minutes: z.number().int().positive(),
  /** 接口时效监控降级起点（查询成功但不可判定：ABR 采样 / 全部组判不了）；null = 未降级 */
  degraded_since: IsoSchema.nullable(),
  latency_ms: z.number().int().nonnegative().nullable(),
  routes: ServerMonitorApiLatencyQuerySchema.nullable(),
  queues: ServerMonitorApiLatencyQuerySchema.nullable(),
  /** 接口按 p95 降序前 SERVER_MONITOR_API_LATENCY_TOP_ROUTES 个，其后为队列（同样按 p95 降序）*/
  items: z.array(ServerMonitorApiLatencyItemSchema),
});
export type ServerMonitorApiLatency = z.infer<typeof ServerMonitorApiLatencySchema>;

/** 概览「接口时效」卡展示的接口数（按 p95 降序）*/
export const SERVER_MONITOR_API_LATENCY_TOP_ROUTES = 20;
/** 概览展示的队列数上限 */
export const SERVER_MONITOR_API_LATENCY_TOP_QUEUES = 20;

export const ServerMonitorPaymentLatencyProviderSchema = z.object({
  provider: ServerMonitorPaymentProviderSchema,
  /** query_failed = 该 provider 本轮查询失败（其余 provider 照常）*/
  state: z.enum(['ok', 'query_failed']),
  /** 查询失败分类（DB_QUERY_FAILED / DB_TIMEOUT），不含连接串 */
  error_code: z.string().nullable(),
  /** 窗口内到达的购买 / 续费事件数 */
  events: z.number().int().nonnegative(),
  /** 统计窗口内已生效（处理耗时 p95 的样本）条数 */
  samples: z.number().int().nonnegative(),
  processing_p95_ms: z.number().nonnegative().nullable(),
  /** 回看 backlog_lookback_hours 内的积压条数 */
  backlog: z.number().int().nonnegative(),
  /** 最老一笔积压的 received_at（无积压为 null）*/
  oldest_backlog_received_at: IsoSchema.nullable(),
  /** 只有 adapty 有送达延迟；其余为 null */
  delivery_samples: z.number().int().nonnegative().nullable(),
  delivery_p95_ms: z.number().nonnegative().nullable(),
  /** 窗口内事件超过单次读取上限，只统计了最新的一批 */
  truncated: z.boolean(),
});
export type ServerMonitorPaymentLatencyProvider = z.infer<
  typeof ServerMonitorPaymentLatencyProviderSchema
>;

export const ServerMonitorPaymentLatencySchema = z.object({
  /**
   * ok = 最近一轮全部 provider 查询成功；partial = 部分失败；query_failed = 全部失败或连库失败；
   * unconfigured = admin 缺 DATABASE_URL；not_run = cron 尚未写入结果
   */
  state: z.enum(['ok', 'partial', 'query_failed', 'unconfigured', 'not_run']),
  computed_at: IsoSchema.nullable(),
  window_minutes: z.number().int().positive(),
  latency_ms: z.number().int().nonnegative().nullable(),
  providers: z.array(ServerMonitorPaymentLatencyProviderSchema),
});
export type ServerMonitorPaymentLatency = z.infer<typeof ServerMonitorPaymentLatencySchema>;

export const ServerMonitorLastTickSchema = z.object({
  at: IsoSchema,
  raw_events: z.number().int().nonnegative(),
  events: z.number().int().nonnegative(),
  latency_ms: z.number().int().nonnegative(),
  truncated: z.boolean(),
  sampled: z.boolean(),
  query_failed: z.boolean(),
  /** 查询失败分类（TELEMETRY_HTTP_STATUS 等），不含 token */
  query_error_code: z.string().nullable(),
});
export type ServerMonitorLastTick = z.infer<typeof ServerMonitorLastTickSchema>;

export const ServerMonitorNotificationSchema = z.object({
  feishu_group_name: z.string(),
  /** webhook 与签名密钥是否都已配置（不回显 URL）*/
  feishu_configured: z.boolean(),
  last_notification: z.object({ at: IsoSchema, ok: z.boolean() }).nullable(),
  last_tick: ServerMonitorLastTickSchema.nullable(),
});
export type ServerMonitorNotification = z.infer<typeof ServerMonitorNotificationSchema>;

export const ServerMonitorOverviewSchema = z.object({
  service: ServerMonitorServiceSchema,
  /** 本环境后台可查看的 service（serverMonitorViewableServices），前端据此渲染环境切换 */
  viewable_services: z.array(ServerMonitorServiceSchema),
  /** 本 admin Worker 的环境 */
  environment: z.string(),
  /** service 是否为本环境 cron 的监控目标；否 → alerts / notification 为 null */
  is_monitor_target: z.boolean(),
  generated_at: IsoSchema,
  /** Cloudflare Workers Observability 日志页（无 account 配置时 null）*/
  dashboard_logs_url: z.string().nullable(),
  health: ServerMonitorHealthSchema,
  alerts: z
    .object({
      active: z.array(ServerMonitorAlertSchema),
      recovered: z.array(ServerMonitorAlertSchema),
    })
    .nullable(),
  rules: z.object({
    /** environment = 本环境 vars 覆盖后的生效值；defaults = 非本环境目标，展示代码默认值 */
    thresholds_source: z.enum(['environment', 'defaults']),
    thresholds: ServerMonitorThresholdsSchema,
    items: z.array(ServerMonitorRuleSchema),
    /** 支付到账时效规则（窗口 / 冷却 / 恢复同 thresholds）*/
    payment_latency: z.object({
      thresholds: ServerMonitorPaymentThresholdsSchema,
      items: z.array(ServerMonitorPaymentRuleSchema),
    }),
    /** 接口时效规则（窗口 / 冷却 / 恢复同 thresholds）*/
    api_latency: z.object({
      thresholds: ServerMonitorApiLatencyThresholdsSchema,
      items: z.array(ServerMonitorApiLatencyRuleSchema),
    }),
  }),
  notification: ServerMonitorNotificationSchema.nullable(),
  /** 最近一轮支付到账时效（cron 写入）；非本环境监控目标为 null */
  payment_latency: ServerMonitorPaymentLatencySchema.nullable(),
  /** 最近一轮接口时效（cron 写入）；非本环境监控目标为 null */
  api_latency: ServerMonitorApiLatencySchema.nullable(),
});
export type ServerMonitorOverview = z.infer<typeof ServerMonitorOverviewSchema>;

export const ServerMonitorLogItemSchema = z.object({
  /** Telemetry `$metadata.id` */
  id: z.string(),
  occurred_at: IsoSchema,
  level: z.string(),
  category: ServerMonitorCategorySchema,
  event: z.string(),
  error_code: z.string().nullable(),
  http_status: z.number().int().nullable(),
  method: z.string().nullable(),
  /** 原始路径（不含 query）*/
  path: z.string().nullable(),
  request_id: z.string().nullable(),
  /** 追查 id 在 Telemetry 里的字段名 */
  request_id_field: z.enum(['requestId', '$metadata.requestId']),
  /** cause JSON 序列化后截断到 SERVER_MONITOR_LOG_CAUSE_MAX_CHARS */
  cause: z.string().nullable(),
  cause_truncated: z.boolean(),
});
export type ServerMonitorLogItem = z.infer<typeof ServerMonitorLogItemSchema>;

export const ServerMonitorLogsResponseSchema = z.object({
  items: z.array(ServerMonitorLogItemSchema),
  next_cursor: z.string().nullable(),
});
export type ServerMonitorLogsResponse = z.infer<typeof ServerMonitorLogsResponseSchema>;
